import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Upload, Search, RefreshCw, Download, Plus, RotateCcw,
  Calendar, IndianRupee, Users, AlertTriangle, CheckCircle, X, 
  UserX, UserCheck, PlusCircle, History, Settings, Loader2, Trash2
} from 'lucide-react';

export default function FMBTakhmeenPage() {
  const { api, isAdmin, user } = useAuth();
  const userRoles = user?.roles || [user?.role].filter(Boolean);
  const isFMBViewer = userRoles.includes('fmb_viewer') && !isAdmin;
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Years management
  const [years, setYears] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [showYearManagement, setShowYearManagement] = useState(false);
  const [newYearForm, setNewYearForm] = useState({ year: '', name: '', zabhiyat_tracking: true });
  
  // Dashboard & Entries
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  
  // Drilldown state
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [drilldownType, setDrilldownType] = useState(null);
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listFilter, setListFilter] = useState('all');
  
  // Upload dialogs
  const [showCurrentUpload, setShowCurrentUpload] = useState(false);
  const [showPreviousUpload, setShowPreviousUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Manual entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [hofSearch, setHofSearch] = useState('');
  const [hofResults, setHofResults] = useState([]);
  const [selectedHof, setSelectedHof] = useState(null);
  const [hofDetails, setHofDetails] = useState(null);
  const [manualForm, setManualForm] = useState({ takhmeen_amount: '', zabhiyat_count: '0', not_going: false });
  const [saving, setSaving] = useState(false);
  
  // Zabhiyat entry
  const [showZabhiyatEntry, setShowZabhiyatEntry] = useState(false);
  const [zabhiyatForm, setZabhiyatForm] = useState({ zabhiyat_add: '0', zabhiyat_done_add: '0' });
  
  // Tiffin warning dialog
  const [showTiffinWarning, setShowTiffinWarning] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Previous years for history
  const previousYears = useMemo(() => 
    years.filter(y => !y.is_active).map(y => y.year).slice(0, 3)
  , [years]);

  // Fetch years - only once
  const fetchYears = useCallback(async () => {
    try {
      setYearsLoading(true);
      const response = await api.get('/fmb/years');
      setYears(response.data.years || []);
      const active = response.data.active_year;
      setActiveYear(active);
      if (!selectedYear) {
        setSelectedYear(active);
      }
    } catch (error) {
      toast.error('Failed to fetch years');
    } finally {
      setYearsLoading(false);
    }
  }, [api]); // Remove selectedYear dependency to prevent re-fetch

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    if (!activeYear) return;
    try {
      setLoading(true);
      const response = await api.get(`/fmb/takhmeen/dashboard?year=${activeYear}`);
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  }, [api, activeYear]);

  // Fetch entries with debounce
  const fetchEntries = useCallback(async () => {
    if (!selectedYear) return;
    try {
      setEntriesLoading(true);
      let endpoint = '/fmb/takhmeen';
      
      if (listFilter === 'going') {
        endpoint = '/fmb/takhmeen/list/going';
      } else if (listFilter === 'not_going') {
        endpoint = '/fmb/takhmeen/list/not-going';
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (listFilter === 'all') {
        params.append('year', selectedYear);
      }
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`${endpoint}?${params}`);
      setEntries(response.data.entries || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch entries');
    } finally {
      setEntriesLoading(false);
    }
  }, [api, selectedYear, page, searchQuery, listFilter]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  // Only fetch dashboard when tab is active AND activeYear is set
  useEffect(() => {
    if (activeYear && activeTab === 'dashboard') {
      fetchDashboard();
    }
  }, [activeYear, activeTab]);

  // Only fetch entries when list tabs are active
  useEffect(() => {
    if (selectedYear && ['list', 'going', 'not_going'].includes(activeTab)) {
      const debounce = setTimeout(fetchEntries, 300);
      return () => clearTimeout(debounce);
    }
  }, [activeTab, selectedYear, page, searchQuery, listFilter]);

  // Search HOF
  useEffect(() => {
    const searchHof = async () => {
      if (hofSearch.length < 2) {
        setHofResults([]);
        return;
      }
      try {
        const response = await api.get(`/fmb/takhmeen/search-hof?query=${encodeURIComponent(hofSearch)}`);
        setHofResults(response.data.results || []);
      } catch (error) {
        console.error('Search error:', error);
      }
    };
    
    const debounce = setTimeout(searchHof, 300);
    return () => clearTimeout(debounce);
  }, [hofSearch, api]);

  // Create new year
  const handleCreateYear = async () => {
    if (!newYearForm.year) {
      toast.error('Please enter a year');
      return;
    }
    try {
      await api.post('/fmb/years', {
        year: newYearForm.year,
        name: newYearForm.name || `Hajj ${newYearForm.year}`,
        zabhiyat_tracking: newYearForm.zabhiyat_tracking,
        is_active: false
      });
      toast.success('Year created');
      setNewYearForm({ year: '', name: '', zabhiyat_tracking: true });
      fetchYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create year');
    }
  };

  // Activate year
  const handleActivateYear = async (year) => {
    try {
      await api.post(`/fmb/years/${year}/activate`);
      toast.success(`Year ${year} activated`);
      fetchYears();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate year');
    }
  };

  // Handle current year upload
  const handleCurrentUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await api.post('/fmb/takhmeen/upload-current', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(response.data);
      toast.success(`Upload complete: ${response.data.success} new, ${response.data.updated} updated`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Handle previous years upload
  const handlePreviousUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await api.post('/fmb/takhmeen/upload-previous', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(response.data);
      toast.success(`Upload complete: ${response.data.success} new, ${response.data.updated} updated`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Select HOF for manual entry
  const handleSelectHof = async (hof) => {
    setSelectedHof(hof);
    setHofSearch('');
    setHofResults([]);
    
    try {
      const response = await api.get(`/fmb/takhmeen/hof-details/${hof.its_id}`);
      setHofDetails(response.data);
      
      if (response.data.current_year?.exists) {
        setManualForm({
          takhmeen_amount: response.data.current_year.takhmeen_amount?.toString() || '',
          zabhiyat_count: response.data.zabhiyat_summary?.total?.toString() || '0',
          not_going: response.data.current_year.not_going || false
        });
      } else {
        setManualForm({ takhmeen_amount: '', zabhiyat_count: '0', not_going: false });
      }
    } catch (error) {
      console.error('Fetch HOF details error:', error);
      toast.error('Failed to fetch HOF details');
    }
  };

  // Save manual entry
  const handleSaveManual = async (force = false) => {
    if (!selectedHof) {
      toast.error('Please select a HOF');
      return;
    }
    
    // If NOT going, skip amount validation and tiffin check
    const isNotGoing = manualForm.not_going === true;
    
    if (!isNotGoing && !manualForm.takhmeen_amount) {
      toast.error('Please enter Takhmeen amount');
      return;
    }
    
    // Only check tiffin for GOING entries
    if (!force && !isNotGoing && hofDetails?.tiffin_status?.has_unpaid) {
      setShowTiffinWarning(true);
      return;
    }
    
    try {
      setSaving(true);
      const response = await api.post('/fmb/takhmeen/manual-entry', {
        hof_its: selectedHof.its_id,
        takhmeen_amount: isNotGoing ? 0 : parseFloat(manualForm.takhmeen_amount),
        zabhiyat_count: isNotGoing ? 0 : parseInt(manualForm.zabhiyat_count) || 0,
        not_going: isNotGoing
      });
      
      const statusMsg = isNotGoing ? ' (Thaali List updated to Not Going)' : ' (Thaali List updated)';
      toast.success((response.data.is_update ? 'Entry updated' : 'Entry created') + statusMsg);
      setShowManualEntry(false);
      resetManualForm();
      fetchDashboard();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Save Zabhiyat entry
  const handleSaveZabhiyat = async () => {
    if (!selectedHof) {
      toast.error('Please select a HOF');
      return;
    }
    
    const zabhiyatAdd = parseInt(zabhiyatForm.zabhiyat_add) || 0;
    const zabhiyatDoneAdd = parseInt(zabhiyatForm.zabhiyat_done_add) || 0;
    
    if (zabhiyatAdd === 0 && zabhiyatDoneAdd === 0) {
      toast.error('Please enter Zabhiyat count to add');
      return;
    }
    
    try {
      setSaving(true);
      const response = await api.post('/fmb/takhmeen/zabhiyat-entry', {
        hof_its: selectedHof.its_id,
        zabhiyat_add: zabhiyatAdd,
        zabhiyat_done_add: zabhiyatDoneAdd
      });
      
      toast.success(`Zabhiyat updated: Total ${response.data.zabhiyat_count}, Done ${response.data.zabhiyat_done}`);
      setShowZabhiyatEntry(false);
      resetManualForm();
      fetchDashboard();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Revert to Going
  const handleRevertToGoing = async (hofIts) => {
    try {
      await api.post('/fmb/takhmeen/revert-to-going', { hof_its: hofIts });
      toast.success('Entry reverted to Going');
      fetchDashboard();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Revert failed');
    }
  };

  // Delete entry
  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    
    try {
      setDeleting(true);
      await api.delete(`/fmb/takhmeen/${entryToDelete.id}`);
      toast.success('Entry deleted successfully');
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
      fetchDashboard();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const resetManualForm = () => {
    setSelectedHof(null);
    setHofDetails(null);
    setHofSearch('');
    setHofResults([]);
    setManualForm({ takhmeen_amount: '', zabhiyat_count: '0', not_going: false });
    setZabhiyatForm({ zabhiyat_add: '0', zabhiyat_done_add: '0' });
  };

  // Drilldown handler - opens detailed list for clicked stat
  const handleDrilldown = async (type) => {
    setDrilldownType(type);
    setShowDrilldown(true);
    setDrilldownLoading(true);
    
    try {
      const response = await api.get(`/fmb/takhmeen/drilldown/${activeYear}?type=${type}`);
      setDrilldownData(response.data.families || []);
    } catch (error) {
      toast.error('Failed to load details');
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Export drilldown data
  const handleDrilldownExport = async (format) => {
    if (!drilldownType) return;
    
    try {
      toast.info(`Generating ${format.toUpperCase()}...`);
      const response = await api.get(`/fmb/takhmeen/drilldown/${activeYear}/export?type=${drilldownType}&format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const typeLabel = {
        'total_hof': 'Total_HOF',
        'takhmeen_done': 'Takhmeen_Done',
        'takhmeen_not_done': 'Takhmeen_Pending',
        'going': 'Going',
        'not_going': 'Not_Going',
        'zabhiyat_pending': 'Zabhiyat_Pending'
      }[drilldownType] || drilldownType;
      link.setAttribute('download', `FMB_${typeLabel}_${activeYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    }
  };

  // Export
  const handleExport = async (format) => {
    try {
      const response = await api.get(`/fmb/takhmeen/export/${selectedYear}?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FMB_Takhmeen_${selectedYear}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (yearsLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="fmb-takhmeen-page">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">Takhmeen & Zabhiyat</h2>
            <p className="text-slate-500 text-xs sm:text-sm">Active Year: <Badge className="bg-blue-500">{activeYear}</Badge></p>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setShowYearManagement(true)} className="w-full sm:w-auto">
              <Settings className="w-4 h-4 mr-1" /> Manage Years
            </Button>
          )}
        </div>
        
        {/* Action Buttons - Mobile Grid */}
        {isAdmin && (
          <div className="grid grid-cols-2 sm:flex gap-2">
            <Button size="sm" onClick={() => setShowManualEntry(true)} className="text-xs sm:text-sm" data-testid="manual-entry-btn">
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Takhmeen
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowZabhiyatEntry(true); resetManualForm(); }} className="text-xs sm:text-sm" data-testid="zabhiyat-entry-btn">
              <PlusCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Zabhiyat
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowCurrentUpload(true); setUploadFile(null); setUploadResults(null); }} className="text-xs sm:text-sm">
              <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Upload
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowPreviousUpload(true); setUploadFile(null); setUploadResults(null); }} className="text-xs sm:text-sm">
              <Upload className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Previous
            </Button>
          </div>
        )}
      </div>

      {/* Tabs - Mobile Scrollable */}
      <Tabs value={activeTab} onValueChange={(v) => { 
        setActiveTab(v); 
        if (v === 'going') setListFilter('going'); 
        else if (v === 'not_going') setListFilter('not_going'); 
        else if (v === 'list') setListFilter('all'); 
      }}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max sm:w-auto">
            <TabsTrigger value="dashboard" className="text-xs sm:text-sm px-2 sm:px-3">Dashboard</TabsTrigger>
            <TabsTrigger value="list" className="text-xs sm:text-sm px-2 sm:px-3">All</TabsTrigger>
            <TabsTrigger value="going" className="text-xs sm:text-sm px-2 sm:px-3 text-green-600">
              <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Going
            </TabsTrigger>
            <TabsTrigger value="not_going" className="text-xs sm:text-sm px-2 sm:px-3 text-amber-600">
              <UserX className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Not Going
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-xs sm:text-sm px-2 sm:px-3 text-purple-600">
              Reports
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4 sm:space-y-6 mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : dashboard && (
            <>
              {/* Summary Cards - Clickable for drilldown */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                {/* Total HOF */}
                <Card 
                  className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('total_hof')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-slate-600 font-medium">Total HOF</p>
                      <p className="text-xl sm:text-3xl font-bold text-slate-700">{dashboard.current_stats.total_hof || 0}</p>
                      <p className="text-[10px] text-slate-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Takhmeen Done */}
                <Card 
                  className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('takhmeen_done')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-blue-600 font-medium">Takhmeen Done</p>
                      <p className="text-xl sm:text-3xl font-bold text-blue-700">{dashboard.current_stats.total_entries || 0}</p>
                      <p className="text-[10px] text-blue-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Takhmeen Not Done */}
                <Card 
                  className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('takhmeen_not_done')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-red-600 font-medium">Takhmeen Pending</p>
                      <p className="text-xl sm:text-3xl font-bold text-red-700">
                        {(dashboard.current_stats.total_hof || 0) - (dashboard.current_stats.total_entries || 0)}
                      </p>
                      <p className="text-[10px] text-red-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Going */}
                <Card 
                  className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('going')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-green-600 font-medium">Going</p>
                      <p className="text-xl sm:text-3xl font-bold text-green-700">{dashboard.current_stats.going_count || 0}</p>
                      <p className="text-[10px] text-green-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Not Going */}
                <Card 
                  className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('not_going')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-amber-600 font-medium">Not Going</p>
                      <p className="text-xl sm:text-3xl font-bold text-amber-700">{dashboard.current_stats.not_going_count || 0}</p>
                      <p className="text-[10px] text-amber-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Zabhiyat Pending */}
                <Card 
                  className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleDrilldown('zabhiyat_pending')}
                >
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-purple-600 font-medium">Zabhiyat Pending</p>
                      <p className="text-xl sm:text-3xl font-bold text-purple-700">{dashboard.current_stats.zabhiyat_pending || 0}</p>
                      <p className="text-[10px] text-purple-400 mt-1">Click to view →</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Financial Summary Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-emerald-600 font-medium">Total Takhmeen</p>
                      <p className="text-base sm:text-2xl font-bold text-emerald-700">₹{(dashboard.current_stats.going_takhmeen || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-teal-600 font-medium">Total Zabhiyat</p>
                      <p className="text-xl sm:text-3xl font-bold text-teal-700">{dashboard.current_stats.going_zabhiyat || 0}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                  <CardContent className="p-3 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-cyan-600 font-medium">Zabhiyat Done</p>
                      <p className="text-xl sm:text-3xl font-bold text-cyan-700">{dashboard.current_stats.zabhiyat_done || 0}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* List Tabs */}
        {['list', 'going', 'not_going'].map(tabId => (
          <TabsContent key={tabId} value={tabId} className="space-y-4 mt-4">
            <EntriesTable 
              entries={entries}
              loading={entriesLoading}
              selectedYear={selectedYear}
              setSelectedYear={tabId === 'list' ? setSelectedYear : null}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              fetchEntries={fetchEntries}
              handleExport={handleExport}
              showYearFilter={tabId === 'list'}
              years={years}
              showNotGoingColumn={tabId === 'list'}
              showRevertButton={tabId === 'not_going'}
              onRevert={handleRevertToGoing}
              onDelete={(entry) => { setEntryToDelete(entry); setShowDeleteConfirm(true); }}
              title={tabId === 'going' ? 'Going List' : tabId === 'not_going' ? 'Not Going List' : undefined}
              isAdmin={isAdmin}
              hideTakhmeen={isFMBViewer}
            />
          </TabsContent>
        ))}

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4 mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : dashboard && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Amount Bifurcation Table */}
              {dashboard.amount_bifurcation && dashboard.amount_bifurcation.length > 0 && (
                <Card>
                  <CardHeader className="py-3 sm:py-4">
                    <CardTitle className="text-sm sm:text-lg">Amount Bifurcation ({activeYear})</CardTitle>
                    <p className="text-xs text-slate-500">Breakdown of takhmeen amounts by contribution level</p>
                  </CardHeader>
                  <CardContent className="p-0 sm:p-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right text-xs sm:text-sm">Amount</TableHead>
                            <TableHead className="text-center text-xs sm:text-sm">Units</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dashboard.amount_bifurcation.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell className="text-right font-medium text-xs sm:text-sm">₹{item.amount?.toLocaleString('en-IN')}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">{item.units}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-emerald-700 text-xs sm:text-sm">₹{item.total?.toLocaleString('en-IN')}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-slate-50 font-bold">
                            <TableCell className="text-right text-xs sm:text-sm">Total</TableCell>
                            <TableCell className="text-center text-xs sm:text-sm">
                              {dashboard.amount_bifurcation.reduce((sum, item) => sum + item.units, 0)}
                            </TableCell>
                            <TableCell className="text-right text-emerald-700 text-xs sm:text-sm">
                              ₹{dashboard.amount_bifurcation.reduce((sum, item) => sum + item.total, 0)?.toLocaleString('en-IN')}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mohalla-wise Summary */}
              {dashboard.mohalla_summary && dashboard.mohalla_summary.length > 0 && (
                <Card>
                  <CardHeader className="py-3 sm:py-4">
                    <CardTitle className="text-sm sm:text-lg">Mohalla-wise Summary ({activeYear})</CardTitle>
                    <p className="text-xs text-slate-500">Breakdown by mohalla</p>
                  </CardHeader>
                  <CardContent className="p-0 sm:p-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs sm:text-sm">Mohalla</TableHead>
                            <TableHead className="text-center text-xs sm:text-sm">Going</TableHead>
                            <TableHead className="text-center text-xs sm:text-sm">Not Going</TableHead>
                            <TableHead className="text-right text-xs sm:text-sm">Takhmeen</TableHead>
                            <TableHead className="text-center text-xs sm:text-sm">Zabhiyat</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dashboard.mohalla_summary.map((m) => (
                            <TableRow key={m.mohalla}>
                              <TableCell className="font-medium text-xs sm:text-sm">{m.mohalla}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">{m.going_count}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">{m.not_going_count}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium text-xs sm:text-sm">₹{m.total_takhmeen?.toLocaleString('en-IN')}</TableCell>
                              <TableCell className="text-center text-xs sm:text-sm">{m.total_zabhiyat}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Year Management Dialog */}
      <Dialog open={showYearManagement} onOpenChange={setShowYearManagement}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage FMB Years</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Create New Year */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <p className="font-medium text-sm">Create New Year</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Year (e.g., 1449H)"
                  value={newYearForm.year}
                  onChange={(e) => setNewYearForm({ ...newYearForm, year: e.target.value })}
                />
                <Input
                  placeholder="Name (optional)"
                  value={newYearForm.name}
                  onChange={(e) => setNewYearForm({ ...newYearForm, name: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="zabhiyat_tracking" 
                  checked={newYearForm.zabhiyat_tracking}
                  onCheckedChange={(checked) => setNewYearForm({ ...newYearForm, zabhiyat_tracking: checked })}
                />
                <Label htmlFor="zabhiyat_tracking" className="text-sm">Enable Zabhiyat Tracking</Label>
              </div>
              <Button size="sm" onClick={handleCreateYear} className="w-full">
                <Plus className="w-4 h-4 mr-1" /> Create Year
              </Button>
            </div>

            {/* Existing Years */}
            <div className="space-y-2">
              <p className="font-medium text-sm">Existing Years</p>
              {years.map((year) => (
                <div key={year.id} className="p-3 border rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-medium">{year.year} {year.is_active && <Badge className="bg-green-500 ml-2">Active</Badge>}</p>
                    <p className="text-xs text-slate-500">
                      {year.stats?.entries || 0} entries | ₹{(year.stats?.total_takhmeen || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  {!year.is_active && (
                    <Button size="sm" variant="outline" onClick={() => handleActivateYear(year.year)}>
                      Activate
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowYearManagement(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Current Year Upload Dialog */}
      <Dialog open={showCurrentUpload} onOpenChange={setShowCurrentUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload - {activeYear}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-lg text-sm">
              <p className="font-medium text-blue-700 mb-2">Required Columns:</p>
              <ul className="list-disc list-inside text-blue-600 space-y-1 text-xs sm:text-sm">
                <li><strong>ITS Number</strong> (required)</li>
                <li><strong>Takhmeen Amount</strong> (required)</li>
                <li><strong>Zabhiyat Count</strong> (optional)</li>
              </ul>
            </div>
            
            <div>
              <Label>Select Excel File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
            </div>
            
            {uploadResults && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ New: {uploadResults.success}</span>
                  <span className="text-blue-600">↻ Updated: {uploadResults.updated}</span>
                  <span className="text-red-600">✗ Errors: {uploadResults.errors?.length || 0}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCurrentUpload(false)}>Cancel</Button>
            <Button onClick={handleCurrentUpload} disabled={uploading || !uploadFile}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Previous Years Upload Dialog */}
      <Dialog open={showPreviousUpload} onOpenChange={setShowPreviousUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload - Previous Years</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 rounded-lg text-sm">
              <p className="font-medium text-amber-700 mb-2">Required Columns:</p>
              <ul className="list-disc list-inside text-amber-600 space-y-1 text-xs sm:text-sm">
                <li><strong>ITS Number</strong> (required)</li>
                <li><strong>Year</strong> (required)</li>
                <li><strong>Takhmeen Amount</strong> (required)</li>
              </ul>
            </div>
            
            <div>
              <Label>Select Excel File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
            </div>
            
            {uploadResults && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ New: {uploadResults.success}</span>
                  <span className="text-blue-600">↻ Updated: {uploadResults.updated}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviousUpload(false)}>Cancel</Button>
            <Button onClick={handlePreviousUpload} disabled={uploading || !uploadFile}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Takhmeen Entry Dialog */}
      <Dialog open={showManualEntry} onOpenChange={(open) => { setShowManualEntry(open); if (!open) resetManualForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Manual Takhmeen Entry - {activeYear}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!selectedHof ? (
              <div>
                <Label className="text-sm">Search HOF (ITS or Name)</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={hofSearch}
                    onChange={(e) => setHofSearch(e.target.value)}
                    className="pl-9"
                    data-testid="hof-search-input"
                  />
                </div>
                
                {hofResults.length > 0 && (
                  <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-auto">
                    {hofResults.map((hof) => (
                      <div
                        key={hof.its_id}
                        className="p-3 hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleSelectHof(hof)}
                        data-testid={`hof-result-${hof.its_id}`}
                      >
                        <p className="font-medium text-sm">{hof.full_name}</p>
                        <p className="text-xs text-slate-500">
                          <span className="font-mono">ITS: {hof.its_id}</span> | {hof.mohalla}
                          {hof.thaali_number && ` | Thaali: ${hof.thaali_number}`}
                        </p>
                        {/* Show note if ITS was mapped from a family member to HOF */}
                        {hof.mapped_from_member && (
                          <p className="text-xs text-amber-600 mt-1 bg-amber-50 px-2 py-1 rounded">
                            ⚠️ {hof.note || `Searched ITS ${hof.original_member_its} belongs to family member. Showing HOF.`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Selected HOF Details - Mobile Optimized */}
                <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-sm sm:text-lg">{hofDetails?.hof_details?.full_name}</p>
                      <div className="grid grid-cols-2 gap-1 sm:gap-2 mt-2 text-xs sm:text-sm">
                        <div><span className="text-slate-500">ITS:</span> <span className="font-mono">{hofDetails?.hof_details?.its_id}</span></div>
                        <div><span className="text-slate-500">Mobile:</span> {hofDetails?.hof_details?.mobile || 'N/A'}</div>
                        <div><span className="text-slate-500">Mohalla:</span> {hofDetails?.hof_details?.mohalla}</div>
                        <div><span className="text-slate-500">Sector:</span> {hofDetails?.hof_details?.sector}</div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={resetManualForm}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {hofDetails?.current_year?.exists && (
                    <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500" />
                      <span className="text-xs sm:text-sm text-blue-700">
                        Existing: ₹{hofDetails.current_year.takhmeen_amount?.toLocaleString('en-IN')}
                        {hofDetails.current_year.not_going && ' (NOT GOING)'}
                      </span>
                    </div>
                  )}
                  
                  {hofDetails?.tiffin_status?.has_unpaid && !manualForm.not_going && (
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-xs sm:text-sm text-amber-700">Tiffin: {hofDetails.tiffin_status.payment_status}</span>
                    </div>
                  )}
                </div>

                {/* 3-Year History */}
                {hofDetails?.takhmeen_history && (
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium text-xs sm:text-sm text-slate-700 mb-2 flex items-center gap-1">
                      <History className="w-3 h-3 sm:w-4 sm:h-4" /> Last 3 Years (Read-Only)
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {hofDetails.takhmeen_history.map((yr) => (
                        <div key={yr.year} className={`p-2 rounded border text-center ${yr.exists ? 'bg-white border-green-200' : 'bg-gray-100 border-gray-200'}`}>
                          <p className="text-xs text-slate-500">{yr.year}</p>
                          <p className={`font-semibold text-xs sm:text-sm ${yr.exists ? 'text-green-700' : 'text-slate-400'}`}>
                            {yr.exists ? `₹${yr.amount?.toLocaleString('en-IN')}` : '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Not Going Checkbox */}
                <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Checkbox 
                    id="not_going" 
                    checked={manualForm.not_going === true}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, not_going: checked === true })}
                    data-testid="not-going-checkbox"
                  />
                  <Label htmlFor="not_going" className="text-amber-700 font-medium cursor-pointer text-sm">
                    Mark as NOT GOING (Skip Tiffin Check)
                  </Label>
                </div>

                {/* Entry Form */}
                {!manualForm.not_going && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Takhmeen Amount *</Label>
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={manualForm.takhmeen_amount}
                        onChange={(e) => setManualForm({ ...manualForm, takhmeen_amount: e.target.value })}
                        className="mt-1"
                        data-testid="takhmeen-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Zabhiyat Count</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={manualForm.zabhiyat_count}
                        onChange={(e) => setManualForm({ ...manualForm, zabhiyat_count: e.target.value })}
                        className="mt-1"
                        data-testid="zabhiyat-count-input"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowManualEntry(false); resetManualForm(); }} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={() => handleSaveManual(false)} disabled={saving || !selectedHof} className="w-full sm:w-auto" data-testid="save-takhmeen-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {manualForm.not_going ? 'Save as Not Going' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zabhiyat Entry Dialog */}
      <Dialog open={showZabhiyatEntry} onOpenChange={(open) => { setShowZabhiyatEntry(open); if (!open) resetManualForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Add Zabhiyat - {activeYear}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-xs sm:text-sm text-blue-700">
              <strong>Note:</strong> Adds to existing count (additive only).
            </div>
            
            {!selectedHof ? (
              <div>
                <Label className="text-sm">Search HOF</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={hofSearch}
                    onChange={(e) => setHofSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {hofResults.length > 0 && (
                  <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-auto">
                    {hofResults.map((hof) => (
                      <div
                        key={hof.its_id}
                        className="p-3 hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleSelectHof(hof)}
                      >
                        <p className="font-medium text-sm">{hof.full_name}</p>
                        <p className="text-xs text-slate-500">ITS: {hof.its_id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{hofDetails?.hof_details?.full_name}</p>
                      <p className="text-xs text-slate-500">ITS: {hofDetails?.hof_details?.its_id}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={resetManualForm}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {hofDetails?.zabhiyat_summary && (
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="p-2 bg-white rounded border text-center">
                        <p className="text-slate-500">Total</p>
                        <p className="font-bold text-lg">{hofDetails.zabhiyat_summary.total}</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded border border-green-200 text-center">
                        <p className="text-green-600">Done</p>
                        <p className="font-bold text-lg text-green-700">{hofDetails.zabhiyat_summary.done}</p>
                      </div>
                      <div className="p-2 bg-orange-50 rounded border border-orange-200 text-center">
                        <p className="text-orange-600">Pending</p>
                        <p className="font-bold text-lg text-orange-700">{hofDetails.zabhiyat_summary.pending}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Add to Total</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={zabhiyatForm.zabhiyat_add}
                      onChange={(e) => setZabhiyatForm({ ...zabhiyatForm, zabhiyat_add: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Add to Done</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={zabhiyatForm.zabhiyat_done_add}
                      onChange={(e) => setZabhiyatForm({ ...zabhiyatForm, zabhiyat_done_add: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowZabhiyatEntry(false); resetManualForm(); }} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleSaveZabhiyat} disabled={saving || !selectedHof} className="w-full sm:w-auto">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-2" />}
              Add Zabhiyat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tiffin Warning Dialog */}
      <AlertDialog open={showTiffinWarning} onOpenChange={setShowTiffinWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Tiffin Amount Unpaid
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tiffin payment is "{hofDetails?.tiffin_status?.payment_status}". Continue anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowTiffinWarning(false); handleSaveManual(true); }}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Takhmeen Entry
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the takhmeen entry for <strong>{entryToDelete?.hof_name}</strong> (ITS: {entryToDelete?.hof_its})?
              <br /><br />
              This action cannot be undone. The family's FMB status will also be reset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEntry} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drilldown Dialog */}
      <Dialog open={showDrilldown} onOpenChange={setShowDrilldown}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drilldownType === 'total_hof' && 'Total HOF List'}
              {drilldownType === 'takhmeen_done' && 'Takhmeen Done List'}
              {drilldownType === 'takhmeen_not_done' && 'Takhmeen Pending List'}
              {drilldownType === 'going' && 'Going List'}
              {drilldownType === 'not_going' && 'Not Going List'}
              {drilldownType === 'zabhiyat_pending' && 'Zabhiyat Pending List'}
              <Badge variant="secondary">{drilldownData.length} records</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {/* Export buttons */}
          <div className="flex gap-2 mb-4">
            <Button onClick={() => handleDrilldownExport('pdf')} size="sm" variant="outline" className="text-red-600">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            {isAdmin && (
              <Button onClick={() => handleDrilldownExport('excel')} size="sm" variant="outline" className="text-green-600">
                <Download className="w-4 h-4 mr-1" /> Excel
              </Button>
            )}
          </div>
          
          {/* Data table */}
          <div className="flex-1 overflow-auto">
            {drilldownLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sr</TableHead>
                    <TableHead>HOF ITS</TableHead>
                    <TableHead>HOF Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Thaali No</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drilldownData.map((item, idx) => (
                    <TableRow key={item.hof_its || idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-mono">{item.hof_its || item.hof_its_id}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{item.hof_name}</TableCell>
                      <TableCell>{item.mobile || '-'}</TableCell>
                      <TableCell>{item.mohalla}</TableCell>
                      <TableCell>{item.sector}</TableCell>
                      <TableCell>{item.thaali_number || '-'}</TableCell>
                      <TableCell>
                        <Badge className={
                          item.takhmeen_status === 'done' ? 'bg-green-100 text-green-700' :
                          item.not_going ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {item.not_going ? 'Not Going' : item.takhmeen_status === 'done' ? 'Takhmeen Done' : 'Pending'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Entries Table Component - Mobile Optimized
function EntriesTable({ 
  entries, 
  loading, 
  selectedYear, 
  setSelectedYear, 
  searchQuery, 
  setSearchQuery, 
  page, 
  setPage, 
  totalPages, 
  fetchEntries, 
  handleExport, 
  showYearFilter = true,
  years = [],
  showNotGoingColumn = true,
  showRevertButton = false,
  onRevert,
  onDelete,
  title,
  isAdmin,
  hideTakhmeen = false
}) {
  return (
    <div className="space-y-3">
      {title && <h3 className="text-sm sm:text-lg font-semibold">{title}</h3>}
      
      {/* Filters - Mobile Stack */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
        {showYearFilter && setSelectedYear && years.length > 0 && (
          <Select value={selectedYear} onValueChange={(v) => { setSelectedYear(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(yr => (
                <SelectItem key={yr.year} value={yr.year}>
                  {yr.year} {yr.is_active && '✓'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Search ITS or Name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchEntries} className="px-3">
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          {handleExport && !hideTakhmeen && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} className="hidden sm:flex">
                <Download className="w-4 h-4 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} className="hidden sm:flex">
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Entries - Mobile Card View / Desktop Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              No entries found
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden divide-y">
                {entries.map((entry) => (
                  <div key={entry.id} className="p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{entry.hof_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{entry.hof_its}</p>
                      </div>
                      {showNotGoingColumn && (
                        <Badge className={`ml-2 text-xs ${entry.not_going ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {entry.not_going ? 'Not Going' : 'Going'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="font-medium">₹{entry.not_going ? '0' : entry.takhmeen_amount?.toLocaleString('en-IN')}</span>
                      <span className="text-purple-600">Zab: {entry.not_going ? '-' : entry.zabhiyat_count}</span>
                    </div>
                    {showRevertButton && isAdmin && entry.not_going && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => onRevert(entry.hof_its)}
                        className="mt-2 text-xs w-full text-green-600"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Revert to Going
                      </Button>
                    )}
                    {isAdmin && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => onDelete(entry)}
                        className="mt-2 text-xs w-full text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Delete Entry
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>HOF ITS</TableHead>
                      <TableHead>HOF Name</TableHead>
                      {!hideTakhmeen && <TableHead className="text-right">Takhmeen</TableHead>}
                      <TableHead className="text-center">Zabhiyat</TableHead>
                      {showNotGoingColumn && <TableHead className="text-center">Status</TableHead>}
                      {isAdmin && <TableHead className="text-center">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono text-sm">{entry.hof_its}</TableCell>
                        <TableCell className="font-medium max-w-[200px]">
                          <span className="block truncate">{entry.hof_name}</span>
                        </TableCell>
                        {!hideTakhmeen && (
                          <TableCell className="text-right font-medium">
                            {entry.not_going ? '-' : `₹${entry.takhmeen_amount?.toLocaleString('en-IN')}`}
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          {entry.not_going ? '-' : (
                            <span>
                              {entry.zabhiyat_count}
                              {entry.zabhiyat_done > 0 && (
                                <span className="text-green-600 text-xs ml-1">({entry.zabhiyat_done})</span>
                              )}
                            </span>
                          )}
                        </TableCell>
                        {showNotGoingColumn && (
                          <TableCell className="text-center">
                            <Badge className={entry.not_going ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                              {entry.not_going ? 'Not Going' : 'Going'}
                            </Badge>
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-center">
                            <div className="flex gap-1 justify-center">
                              {showRevertButton && entry.not_going && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => onRevert(entry.hof_its)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => onDelete(entry)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Prev
          </Button>
          <span className="px-3 py-2 text-xs sm:text-sm">
            {page} / {totalPages}
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
